import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { applyOffers, computeTotals, type Applied, type BillLine, type Offer, type Totals } from '@sense/shared';
import { packLines, useCart, type CartLine } from './cart';
import { db } from './db';

export interface Priced {
  items: BillLine[];     // packed lines, disc = manual + offer
  autoLine: number[];    // offer discount per line
  lineOfferName: Array<string | undefined>;
  autoBill: number;      // offer discount on the bill
  manual: number;        // manual line + bill discounts
  billDiscount: number;  // manual bill discount + offer bill discount
  applied: Applied[];
  totals: Totals;
}

export function priceCart(lines: CartLine[], manualBillDisc: number, offers: Offer[], now = new Date()): Priced {
  const packed = packLines(lines);
  const r = applyOffers(packed, lines.map((l) => l.category ?? ''), offers, now, manualBillDisc);
  const items = packed.map((l, i) => [l[0], l[1], l[2], l[3] + r.lineDisc[i], l[4]] as BillLine);
  const billDiscount = manualBillDisc + r.billDisc;
  return {
    items,
    autoLine: r.lineDisc,
    lineOfferName: r.lineOffer.map((id) => (id == null ? undefined : r.applied.find((a) => a.offerId === id)?.name)),
    autoBill: r.billDisc,
    manual: packed.reduce((s, l) => s + l[3], 0) + manualBillDisc,
    billDiscount,
    applied: r.applied,
    totals: computeTotals(items, billDiscount),
  };
}

export function useOffers(): Offer[] {
  return useLiveQuery(() => db.offers.toArray(), []) ?? [];
}

/** The current cart priced with live offers. Re-evaluates every minute so happy hours switch on time. */
export function usePricedCart(): Priced {
  const { lines, billDiscount } = useCart();
  const offers = useOffers();
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60000));
  useEffect(() => {
    const t = setInterval(() => setMinute(Math.floor(Date.now() / 60000)), 15000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => priceCart(lines, billDiscount, offers, new Date(minute * 60000)), [lines, billDiscount, offers, minute]);
}
